import { IconChevronRight } from "@/assets/icons/geist/IconChevronRight";
import {
    StepAutomations,
    StepClone,
    StepPrebuild,
    StepSecrets,
    StepStart,
    StepStartDevcontainer,
} from "@/components/environments/EnvironmentStartSteps";
import { EnvironmentSwitch } from "@/components/environments/EnvironmentSwitch";
import { Button } from "@/components/flexkit/Button";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { SkeletonBlock, SkeletonText } from "@/components/podkit/loading/Skeleton";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { duration, timeAgo } from "@/format/time";
import { useEnvironment, useUpdateEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { useRunner, type PlainRunner } from "@/queries/runner-queries";
import {
    effectiveState,
    hasFailureMessageFromSameSession,
    hasWarningMessageFromSameSession,
    type EffectiveState,
} from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";
import { Timestamp, type Duration, type PlainMessage } from "@bufbuild/protobuf";
import { useCallback, useEffect, useMemo, useState, type FC, type PropsWithChildren } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { useStreamLogs } from "@/hooks/use-stream-logs";
import { environmentLogsUrl } from "@/utils/environment";
import { latestLogLinePerStep } from "@/components/environments/environment-start-steps-latest-log-line";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { stepDevcontainerState } from "@/components/environments/environment-start-steps-state";
import { Collapsible, CollapsibleContent } from "@/components/flexkit/Collapsible";
import { useGetOrganizationPolicies, type PlainOrganizationPolicies } from "@/queries/organization-queries";

export const EnvironmentStartSequence: FC<{ environmentId: string; onShowSecrets: () => void }> = ({
    environmentId,
    onShowSecrets,
}) => {
    const { data: environment } = useEnvironment(environmentId);
    const { data: runner } = useRunner(environment?.metadata?.runnerId);

    const state = useMemo(() => {
        if (environment) {
            return effectiveState(environment);
        }
    }, [environment]);

    if (!runner || !environment) {
        return <EnvironmentStartSequenceLoading />;
    }

    return (
        <FoldableSequence
            environment={environment}
            runner={runner}
            state={state}
            onShowSecrets={onShowSecrets}
            key={environment?.id}
        />
    );
};

function shouldBeExpanded(environment: PlainEnvironment, canRebuild: boolean, state?: EffectiveState) {
    const hasFailures = hasFailureMessageFromSameSession(environment);
    const hasWarnings = hasWarningMessageFromSameSession(environment);

    // If there's anything interesting in the start sequence, then we want it to be expanded.
    if (hasFailures || hasWarnings || canRebuild) {
        return true;
    }

    // If it's starting or updating then we want it to be expanded so the user can follow along in the progress
    if (state?.state && [EnvironmentPhase.STARTING, EnvironmentPhase.UPDATING].includes(state?.state)) {
        return true;
    }

    // Otherwise we want it to be collapsed by default.
    return false;
}

const FoldableSequence: FC<{
    state?: EffectiveState;
    environment: PlainEnvironment;
    runner: PlainRunner;
    onShowSecrets: () => void;
}> = ({ environment, runner, state, onShowSecrets }) => {
    const { canRebuild } = useMemo(() => stepDevcontainerState(environment), [environment]);
    const [expanded, setExpanded] = useState({
        expanded: shouldBeExpanded(environment, canRebuild, state),
        byUser: false,
    });

    const logsUrl = useMemo(() => {
        // We don't want to attempt to stream logs if the environment is stopped or deleted
        if (state?.state === EnvironmentPhase.STOPPED || state?.state === EnvironmentPhase.DELETED) {
            return undefined;
        }
        return environmentLogsUrl(environment);
    }, [environment, state?.state]);
    const { logGroups } = useStreamLogs({ environment, logsUrl, maxLinesPerGroup: 5 });
    const logLinesPerStep = useMemo(() => latestLogLinePerStep(logGroups), [logGroups]);

    const toggleExpanded = useCallback(() => {
        setExpanded(({ expanded }) => ({ expanded: !expanded, byUser: true }));
    }, []);

    useEffect(() => {
        // Never interfere if the user has manually expanded/unexpanded the sequence
        if (expanded.byUser) {
            return;
        }

        const newExpanded = shouldBeExpanded(environment, canRebuild, state);
        if (newExpanded !== expanded.expanded) {
            setExpanded({ expanded: newExpanded, byUser: false });
        }
    }, [canRebuild, environment, expanded.byUser, expanded.expanded, state]);

    return (
        <Collapsible
            open={expanded.expanded}
            data-testid="environment-start-sequence"
            translate="no"
            className="rounded-xl border-[0.5px] border-border-base bg-surface-secondary transition-shadow hover:shadow"
        >
            <Header
                environment={environment}
                state={state}
                expanded={expanded.expanded}
                onClick={toggleExpanded}
                data-tracking-id-none
            />
            <CollapsibleContent>
                <div className="w-full border-t-[0.5px] border-border-base pb-[20px]" />
                <StepPrebuild
                    prebuildId={environment.metadata?.prebuildId}
                    environmentId={environment.id}
                    key={`step-prebuild-${environment.id}`}
                />
                <StepStart
                    environment={environment}
                    runner={runner}
                    key={`step-machine-${environment.id}`}
                    latestLogLine={logLinesPerStep["start-machine"].line}
                />
                <StepClone
                    environment={environment}
                    key={`step-clone-${environment.id}`}
                    latestLogLine={logLinesPerStep.clone.line}
                />
                <StepSecrets
                    environment={environment}
                    key={`step-secrets-${environment.id}`}
                    onShowSecrets={onShowSecrets}
                />
                <StepAutomations
                    environment={environment}
                    key={`step-automations-${environment.id}`}
                    latestLogLine={logLinesPerStep["load-automations"].line}
                />
                <StepStartDevcontainer
                    environment={environment}
                    key={`step-dev-container-${environment.id}`}
                    latestLogLine={logLinesPerStep["start-dev-container"].line}
                />
                <div className="w-full pb-[20px]" />
            </CollapsibleContent>
        </Collapsible>
    );
};

const EnvironmentStartSequenceLoading: FC = () => {
    return (
        <Container>
            <div className="border-border flex w-full items-center gap-2 rounded-t-xl bg-surface-glass px-4 py-3">
                <div className="flex size-6 items-center justify-center">
                    <SkeletonBlock className="size-5" ready={false} />
                </div>
                <SkeletonText size="lg" ready={false} className="w-20" />
            </div>
            <div className="flex flex-col border-t border-border-base">
                {["w-20", "w-80", "w-72"].map((w, i) => (
                    <div key={i} className="flex h-10 w-full items-center gap-2 px-4 py-2">
                        <div className="flex size-6 items-center justify-center">
                            <SkeletonBlock className="size-5" ready={false} />
                        </div>
                        <SkeletonText size="lg" ready={false} className={w} />
                    </div>
                ))}
            </div>
        </Container>
    );
};

const Container: FC<PropsWithChildren<{ state?: EffectiveState }>> = ({ children }) => {
    return <div className="flex w-full flex-col rounded-xl border border-border-base bg-surface-glass">{children}</div>;
};

const Header: FC<{
    state?: EffectiveState;
    environment: PlainEnvironment;
    onClick?: () => void;
    expanded: boolean;
}> = ({ state, environment, expanded, onClick }) => {
    return (
        <div className={"flex min-h-20 w-full items-center gap-2 rounded-t-xl @container"}>
            <div className="flex w-full min-w-0 shrink flex-wrap items-center gap-2 px-[24px] py-[20px]">
                <div className="flex h-full shrink flex-row items-center gap-3">
                    <EnvironmentSwitch effectiveState={state} environment={environment} key={environment.id} />
                </div>
                <div className="h-2 @lg:h-12 flex-grow cursor-pointer" onClick={onClick} data-tracking-id-none />
                <div className="flex min-w-0 shrink flex-wrap items-center gap-[20px] @lg:gap-[46px]">
                    <AutoStop key={`${environment.id}-auto-stop`} environment={environment} />
                    <Created environment={environment} />
                    <LastStartedAt environment={environment} />
                </div>
            </div>
            <div className="flex h-full w-16 items-center">
                <Button
                    variant={"ghost"}
                    size="md"
                    className={cn("border-0 hover:bg-transparent")}
                    onClick={onClick}
                    aria-label={expanded ? "Collapse environment start sequence" : "Expand environment start sequence"}
                    data-tracking-id-none
                    asChild
                >
                    <button>
                        <IconChevronRight
                            size="base"
                            className={cn(
                                "transition-transform duration-200",
                                expanded ? "rotate-90" : "-rotate-90",
                            )}
                        />
                    </button>
                </Button>
            </div>
        </div>
    );
};

const Created: FC<{ environment: PlainEnvironment }> = ({ environment }) => {
    const date = new Timestamp(environment.metadata?.createdAt).toDate();
    return (
        <div className="flex min-w-0 flex-col">
            <Text className="truncate text-sm text-content-secondary">Created</Text>
            <Text className="truncate text-base font-bold text-content-primary" title={date.toISOString()}>
                {timeAgo(date)}
            </Text>
        </div>
    );
};

const LastStartedAt: FC<{ environment: PlainEnvironment }> = ({ environment }) => {
    const lastStartedAt = environment.metadata?.lastStartedAt;
    if (!lastStartedAt) {
        return;
    }
    const date = new Timestamp(lastStartedAt).toDate();
    return (
        <div className="flex min-w-0 flex-col">
            <Text className="truncate text-sm text-content-secondary">Last started</Text>
            <Text className="truncate text-base font-bold text-content-primary" title={date.toISOString()}>
                {timeAgo(date)}
            </Text>
        </div>
    );
};

const AutoStop: FC<{ environment: PlainEnvironment } & PropsWithClassName> = ({ className, environment }) => {
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const { data: policies } = useGetOrganizationPolicies();
    const timeoutValues = computeTimeoutSelect(environment, policies);
    const [value, setValue] = useState<{ label: string; value: string } | undefined>(initialTimeoutValue(environment));

    // Sync local state with environment data when it changes (e.g., from other tabs)
    useEffect(() => {
        const newValue = initialTimeoutValue(environment);
        setValue(newValue);
    }, [environment]);

    const updateEnvironment = useUpdateEnvironment();
    const handleOnValueChanged = useCallback(
        async (newValue: string) => {
            const original = value;
            try {
                const seconds = BigInt(newValue);
                setValue(toTimeout(Number(newValue)));
                await updateEnvironment.mutateAsync({
                    req: {
                        environmentId: environment.id,
                        spec: { timeout: { disconnected: { seconds } } },
                    },
                });
                toast({
                    title: "Successfully updated auto-stop",
                });
            } catch (error) {
                setValue(original);
                toast({
                    title: "Failed to update auto-stop value",
                    description: formatError(error),
                });
            }
        },
        [value, updateEnvironment, environment.id, toast],
    );

    return (
        <div className={cn("flex min-w-0 flex-col", className)}>
            <div
                className="inline-block text-nowrap text-sm text-content-secondary"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen((o) => !o);
                }}
                data-tracking-id="auto-stop-after-environment-start-sequence"
            >
                Auto-stop after
            </div>
            <Select value={value?.value || ""} onValueChange={handleOnValueChanged} onOpenChange={setOpen} open={open}>
                <SelectTrigger loading={false} className="border-0 bg-transparent p-0 ring-0 focus-visible:ring-0">
                    <span className="inline-block truncate font-bold text-content-primary">
                        <SelectValue className="truncate text-content-primary" placeholder={"Select a timeout"}>
                            {value?.label || ""}
                        </SelectValue>
                    </span>
                    <SelectContent>
                        <SelectGroup>
                            {timeoutValues.map((t) => (
                                <SelectItem key={t.value} value={t.value}>
                                    <div className="flex w-full flex-row items-center justify-between truncate pr-10">
                                        <div>{t.label}</div>
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectGroup>
                    </SelectContent>
                </SelectTrigger>
            </Select>
        </div>
    );
};

function initialTimeoutValue(environment: PlainEnvironment) {
    const timeout = toTimeoutValue(environment?.spec?.timeout?.disconnected);
    return toTimeout(timeout);
}

function computeTimeoutSelect(
    environment: PlainEnvironment,
    policies?: PlainOrganizationPolicies,
): { label: string; value: string }[] {
    const maxTimeoutPolicy = Number(policies?.maximumEnvironmentTimeout?.seconds || 0);
    const timeout = toTimeoutValue(environment?.spec?.timeout?.disconnected);
    let timeouts: number[] = [30 * 60, 60 * 60, 60 * 60 * 3, 60 * 60 * 8];
    if (maxTimeoutPolicy > 0) {
        timeouts = timeouts.filter((seconds) => seconds <= maxTimeoutPolicy);
    }
    const customTimeout = timeout !== 0 && timeouts.find((seconds) => seconds === timeout) === undefined;
    if (customTimeout) {
        timeouts.push(timeout);
        timeouts.sort((a, b) => a - b);
    }
    if (maxTimeoutPolicy === 0) {
        timeouts.push(0);
    }
    return timeouts.map(toTimeout);
}

function toTimeout(seconds: number): { label: string; value: string } {
    if (seconds <= 0) {
        return {
            label: "Never",
            value: "0",
        };
    }
    return {
        label: duration(seconds) + " of inactivity",
        value: String(seconds),
    };
}

function toTimeoutValue(duration?: PlainMessage<Duration>): number {
    return duration === undefined ? 30 * 60 : Number(duration.seconds);
}
